import {useState} from "react";
import {UnitUpgradeClasses, UnitUpgradeStat, UnitUpgradeStats, UnitUpgradeTrack} from "../../models/remoteConstants.model";
import {useApp} from "../../app.context";
import {useIntegrator} from "../../integrator.context";
import Collapsible from "../../elements/collapsible";
import IconButton from "../../elements/IconButton";
import NumberInput from "../../elements/numberInput.element";
import LocalizationInput from "../../elements/localizationInput.element";
import LocalizationTextArea from "../../elements/localizationTextArea.element";
import {AddUnitUpgradeTrackCommand} from "../../commands/addUnitUpgradeTrack.command";
import {UpdateUnitUpgradeTrackCommand} from "../../commands/updateUnitUpgradeTrack.command";
import {RemoveUnitUpgradeTrackCommand} from "../../commands/removeUnitUpgradeTrack.command";
import {AddUnitUpgradeLevelCommand} from "../../commands/addUnitUpgradeLevel.command";
import {RemoveUnitUpgradeLevelCommand} from "../../commands/removeUnitUpgradeLevel.command";
import {trackTotals, unitsOfType} from "../../utils/unitUpgrades";

const STAT_LABEL: Record<UnitUpgradeStat, string> = {attack: 'Attack', defense: 'Defence'};

// A fixed dropdown whose options are shown as they are (not run through localization, whose tables may
// hold an entity of the same name), as passiveRewardCard's RawSelectLabel.
function RawSelect(props: { value: string; options: readonly string[]; labels?: Record<string, string>; onChange(v: string): void }) {
    return (
        <select value={props.value} onChange={e => props.onChange(e.target.value)}>
            {props.options.map(o => <option key={o} value={o}>{props.labels?.[o] ?? o}</option>)}
        </select>
    );
}

// The unit types a track can name: the three classes, then every other hidden unit type the manifest knows.
function useUnitTypeOptions(): string[] {
    const {attributeManifest} = useApp();
    const hidden = (attributeManifest?.UnitAttributes ?? []).filter(e => e.Hidden).map(e => e.TypeName);
    return Array.from(new Set<string>([...UnitUpgradeClasses, ...hidden]));
}

function TrackCard(props: { track: UnitUpgradeTrack; typeOptions: string[]; defaultOpen: boolean }) {
    const {state, executeCommand} = useIntegrator();
    const {getLocalizationValue, getFieldConstraint, confirmDelete} = useApp();
    const {track} = props;
    const totals = trackTotals(track);
    const covered = unitsOfType(state, track.unitType, track.stat);
    const duplicate = (state.unitUpgrades ?? []).some(t => t !== track && t.unitType === track.unitType && t.stat === track.stat);

    const update = (mutate: (t: UnitUpgradeTrack) => void) => executeCommand(new UpdateUnitUpgradeTrackCommand(track.uid, mutate));
    const name = getLocalizationValue(track.uid) || track.uid;

    const header = (
        <div className={'layout horizontal'} style={{gap: 10, alignItems: 'center', flexGrow: 1}}>
            <p style={{flexGrow: 1}}>
                <b>{name}</b>
                <span className={'muted-text'}>{`  ·  ${track.unitType} ${STAT_LABEL[track.stat] ?? track.stat}  ·  ${totals.levels} level${totals.levels === 1 ? '' : 's'}  ·  ${totals.price} gold  ·  ${totals.turns} turns  ·  +${totals.amount}`}</span>
            </p>
            <IconButton icon={'trash'} onClick={async (e) => {
                e.stopPropagation();
                if (await confirmDelete(name, 'upgrade track'))
                    executeCommand(new RemoveUnitUpgradeTrackCommand(track.uid));
            }}/>
        </div>
    );

    return (
        <div className={'box'} style={{padding: '8px 12px'}}>
            <Collapsible title={header} defaultOpen={props.defaultOpen}>
                <div className={'layout vertical gap'} style={{gap: 8, marginTop: 8}}>
                    <div className={'layout horizontal center'} style={{gap: 8}}>
                        <p style={{width: 90}}>Name</p>
                        <LocalizationInput id={track.uid}/>
                    </div>
                    <div className={'layout horizontal'} style={{gap: 8}}>
                        <p style={{width: 90}}>Description</p>
                        <LocalizationTextArea id={`${track.uid}.description`}/>
                    </div>
                    <div className={'layout horizontal'} style={{gap: 8, alignItems: 'center'}}>
                        <p style={{width: 90}}>Unit Type</p>
                        <RawSelect value={track.unitType} options={props.typeOptions} onChange={v => update(t => { t.unitType = v; })}/>
                        <p style={{width: 50, marginLeft: 16}}>Stat</p>
                        <RawSelect value={track.stat} options={UnitUpgradeStats} labels={STAT_LABEL}
                                   onChange={v => update(t => { t.stat = v as UnitUpgradeStat; })}/>
                    </div>
                    <p className={'muted-text'} style={{fontSize: 12}}>
                        {covered.length > 0
                            ? `Improves ${covered.length} unit${covered.length === 1 ? '' : 's'}: ${covered.map(u => getLocalizationValue(u.id) || u.id).join(', ')}`
                            : ''}
                    </p>
                    {covered.length === 0 &&
                        <p style={{color: '#ff6666', fontSize: 12}}>
                            <span className={'fa-solid fa-triangle-exclamation'} style={{marginRight: 6}}/>
                            {`No unit is ${track.unitType}${track.stat === 'attack' ? ' with an attack' : ''}: this track does nothing. Give units the type in the Units tab.`}
                        </p>}
                    {duplicate &&
                        <p style={{color: '#ffaa33', fontSize: 12}}>
                            <span className={'fa-solid fa-triangle-exclamation'} style={{marginRight: 6}}/>
                            {`Another track also improves ${track.unitType} ${STAT_LABEL[track.stat]}; both apply.`}
                        </p>}

                    <table style={{borderCollapse: 'collapse'}}>
                        <thead>
                        <tr>
                            <th style={{textAlign: 'left', width: 60}}><p className={'muted-text'}>Level</p></th>
                            <th style={{textAlign: 'left'}}><p className={'muted-text'}>Price (gold)</p></th>
                            <th style={{textAlign: 'left'}}><p className={'muted-text'}>Turns</p></th>
                            <th style={{textAlign: 'left'}}><p className={'muted-text'}>Adds</p></th>
                            <th/>
                        </tr>
                        </thead>
                        <tbody>
                        {track.levels.map((level, i) => (
                            <tr key={i}>
                                <td><p>{i + 1}</p></td>
                                <td><NumberInput value={level.price} constraint={getFieldConstraint('upgradelevel.price')}
                                                 onChange={v => update(t => { t.levels[i].price = v; })}/></td>
                                <td><NumberInput value={level.turns} constraint={getFieldConstraint('upgradelevel.turns')}
                                                 onChange={v => update(t => { t.levels[i].turns = v; })}/></td>
                                <td><NumberInput value={level.amount} constraint={getFieldConstraint('upgradelevel.amount')}
                                                 onChange={v => update(t => { t.levels[i].amount = v; })}/></td>
                                <td><IconButton icon={'trash'} onClick={() => executeCommand(new RemoveUnitUpgradeLevelCommand(track.uid, i))}/></td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                    <button style={{alignSelf: 'flex-start'}} onClick={() => executeCommand(new AddUnitUpgradeLevelCommand(track.uid))}>
                        Add Level
                    </button>
                </div>
            </Collapsible>
        </div>
    );
}

// The Unit Upgrades tab: the tracks researched at the Forge (a building with the UnitUpgrades attribute), one
// card each. Rift.Sim.SimUpgrades plays them; Documentation/unit_upgrades.md has the rules.
function UnitUpgradesEditorView() {
    const {state, executeCommand} = useIntegrator();
    const typeOptions = useUnitTypeOptions();
    const [newType, setNewType] = useState<string>(UnitUpgradeClasses[0]);
    const [newStat, setNewStat] = useState<UnitUpgradeStat>('attack');
    const [openUid, setOpenUid] = useState<string | undefined>(undefined);

    const forges = state.buildings.filter(b => (b.attributes ?? []).some(a =>
        a === 'UnitUpgrades' || state.buildingAttributes?.find(m => m.uid === a)?.id === 'UnitUpgrades'));

    function addTrack() {
        const command = new AddUnitUpgradeTrackCommand(newType, newStat);
        executeCommand(command);
        setOpenUid(command.newUid);
    }

    return (
        <div className={'layout vertical box flex-grow'} style={{gap: 10, minHeight: 0}}>
            <div className={'layout horizontal center'} style={{gap: 8}}>
                <p className={'text header'} style={{flexGrow: 1}}>Unit Upgrades</p>
                <RawSelect value={newType} options={typeOptions} onChange={setNewType}/>
                <RawSelect value={newStat} options={UnitUpgradeStats} labels={STAT_LABEL} onChange={v => setNewStat(v as UnitUpgradeStat)}/>
                <button onClick={addTrack}>Add Track</button>
            </div>
            <p className={'muted-text'} style={{fontSize: 12}}>
                Researched one at a time at a Forge: any building with the UnitUpgrades attribute (Buildings tab).
                Each level adds its amount to the stat of every unit of the type the player owns, units built later
                included, and is kept if the Forge is lost. Only units given the track's type take part (Units tab,
                Types); an untyped unit gets no upgrades.
            </p>
            {forges.length === 0 &&
                <p style={{color: '#ffaa33', fontSize: 12}}>
                    <span className={'fa-solid fa-triangle-exclamation'} style={{marginRight: 6}}/>
                    No building has the UnitUpgrades attribute, so nothing can be researched. Give it to the Forge in the Buildings tab.
                </p>}
            <div className={'layout vertical'} style={{gap: 8, overflowY: 'auto', flexGrow: 1, height: 0}}>
                {(state.unitUpgrades ?? []).map(track =>
                    <TrackCard key={track.uid} track={track} typeOptions={typeOptions} defaultOpen={track.uid === openUid}/>)}
            </div>
        </div>
    );
}

export default UnitUpgradesEditorView;
